import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Shirt,
  Utensils,
  Cpu,
  Tv,
  Hammer,
  Box,
  Bike,
  Microwave,
  Refrigerator,
  Accessibility,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Lock,
  ShieldAlert,
  Ticket,
  X,
  Percent,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, formatEUR } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import useContent from "../lib/content";
import PageSEO from "../components/SEO";
import NextDepartureBanner from "../components/NextDepartureBanner";

// ---------- Custom SVG icons ----------

/**
 * Kick scooter icon (Phase 46b). Lucide has no scooter glyph, so we
 * ship a tiny inline SVG. Mirrors the lucide-react API — accepts
 * ``className`` and ``strokeWidth`` — so it drops into ``TileGroup``
 * exactly like the other icons.
 */
const PatineteIcon = ({ className = "", strokeWidth = 2 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {/* Handlebar */}
    <line x1="12" y1="4" x2="18" y2="4" />
    {/* Mast */}
    <line x1="16" y1="4" x2="16" y2="17" />
    {/* Deck */}
    <line x1="6" y1="18" x2="16" y2="17" />
    {/* Rear wheel */}
    <circle cx="6" cy="19" r="2.2" />
    {/* Front wheel */}
    <circle cx="18" cy="19" r="2.2" />
  </svg>
);

// ---------- Category / subcategory catalog (Phase 46b) ----------

const CATEGORIES = [
  {
    code: "ropa",
    label: "Ropa",
    Icon: Shirt,
    bg: "bg-purple-100",
    color: "text-purple-600",
  },
  {
    code: "alimentos",
    label: "Alimentos",
    Icon: Utensils,
    bg: "bg-emerald-100",
    color: "text-emerald-600",
  },
  {
    code: "electronica",
    label: "Electrónica",
    Icon: Cpu,
    bg: "bg-blue-100",
    color: "text-blue-600",
  },
  {
    code: "tv",
    label: "TV",
    Icon: Tv,
    bg: "bg-pink-100",
    color: "text-pink-500",
  },
  {
    code: "herramientas",
    label: "Herramientas",
    Icon: Hammer,
    bg: "bg-orange-100",
    color: "text-orange-500",
  },
  {
    code: "otros",
    label: "Otros",
    Icon: Box,
    bg: "bg-slate-100",
    color: "text-slate-500",
  },
];

const ELECTRONICS_SUBCATS = [
  { code: "general", label: "General", Icon: Cpu, bg: "bg-blue-100", color: "text-blue-600" },
  { code: "bicis", label: "Bicis", Icon: Bike, bg: "bg-emerald-100", color: "text-emerald-600" },
  { code: "microondas", label: "Microondas", Icon: Microwave, bg: "bg-orange-100", color: "text-orange-500" },
  { code: "neveras", label: "Neveras", Icon: Refrigerator, bg: "bg-cyan-100", color: "text-cyan-600" },
  { code: "patinetes", label: "Patinetes", Icon: PatineteIcon, bg: "bg-purple-100", color: "text-purple-600" },
  { code: "silla_ruedas", label: "Silla de ruedas", Icon: Accessibility, bg: "bg-pink-100", color: "text-pink-500" },
];

// ---------- Pricing tables (mirror /app/backend/services/shipment_pricing.py) ----------

const GENERIC_BASE_EUR = 35.0;
const GENERIC_INCLUDED_KG = 10;
const GENERIC_EXTRA_PER_KG = 2.9;
const TV_PER_INCH = 2.0;
const BIKE_PER_INCH = 2.0;
const MICROWAVE_PER_L = 1.0;

const TV_OPTIONS = [32, 40, 43, 50, 55, 65, 75, 85].map((i) => ({
  size_value: i,
  size_label: `${i}"`,
  price: i * TV_PER_INCH,
  hint: `${i} pulgadas`,
}));

const BIKE_OPTIONS = [
  { size_value: 14, size_label: 'S · 14"', hint: "Altura: menos de 160 cm" },
  { size_value: 16, size_label: 'S · 16"', hint: "Altura: 160-170 cm" },
  { size_value: 17, size_label: 'M · 17"', hint: "Altura: 165-175 cm" },
  { size_value: 18, size_label: 'M-L · 18"', hint: "Altura: 170-180 cm" },
  { size_value: 19, size_label: 'M-L · 19"', hint: "Altura: 175-180 cm" },
  { size_value: 20, size_label: 'L · 20"', hint: "Altura: 180-185 cm" },
  { size_value: 21, size_label: 'L-XL · 21"', hint: "Altura: 180-190 cm" },
  { size_value: 22, size_label: 'XL · 22"', hint: "Altura: más de 185 cm" },
].map((o) => ({ ...o, price: o.size_value * BIKE_PER_INCH }));

const MICROWAVE_OPTIONS = [
  { size_value: 35, size_label: "Pequeño", hint: "35 L · Microondas de sobremesa", price: 35 * MICROWAVE_PER_L },
  { size_value: 50, size_label: "Mediano", hint: "50 L · Horno mediano", price: 50 * MICROWAVE_PER_L },
  { size_value: 70, size_label: "Grande", hint: "70 L · Horno grande", price: 70 * MICROWAVE_PER_L },
];

const FRIDGE_OPTIONS = [
  { size_label: "Pequeña", hint: "Mini · bajo encimera", price: 70 },
  { size_label: "Mediana", hint: "145-160 cm", price: 100 },
  { size_label: "Grande", hint: "170-190 cm · combi / americana", price: 140 },
];

const SCOOTER_OPTIONS = [
  { size_label: "Pequeño", hint: "Compacto, fácil de transportar", price: 50 },
  { size_label: "Mediano", hint: "Tamaño estándar", price: 75 },
  { size_label: "Grande", hint: "Mayor tamaño o autonomía", price: 110 },
];

const WHEELCHAIR_OPTIONS = [
  { size_label: "Plegable / ligera", hint: "Se pliega, fácil de transportar", price: 40 },
  { size_label: "Estándar eléctrica", hint: "Tamaño habitual", price: 70 },
  { size_label: "Grande / reforzada", hint: "Mayor tamaño o peso (tipo scooter)", price: 160 },
];

// ---------- Pricing helpers (mirror backend) ----------

function computeGenericWeight(kg) {
  const w = parseFloat(String(kg).replace(",", "."));
  if (!w || w <= 0) return 0;
  if (w <= GENERIC_INCLUDED_KG) return GENERIC_BASE_EUR;
  return Number(
    (GENERIC_BASE_EUR + (w - GENERIC_INCLUDED_KG) * GENERIC_EXTRA_PER_KG).toFixed(2)
  );
}

function computePrice(form) {
  const { shipment_type, shipment_subcategory, size_value, size_label, weight_kg } = form;
  if (!shipment_type) return 0;
  if (shipment_type === "tv") return Number((size_value || 0) * TV_PER_INCH) || 0;
  if (shipment_type === "electronica") {
    if (shipment_subcategory === "bicis") return Number((size_value || 0) * BIKE_PER_INCH) || 0;
    if (shipment_subcategory === "microondas") return Number((size_value || 0) * MICROWAVE_PER_L) || 0;
    if (shipment_subcategory === "neveras") {
      const opt = FRIDGE_OPTIONS.find((o) => o.size_label === size_label);
      return opt ? opt.price : 0;
    }
    if (shipment_subcategory === "patinetes") {
      const opt = SCOOTER_OPTIONS.find((o) => o.size_label === size_label);
      return opt ? opt.price : 0;
    }
    if (shipment_subcategory === "silla_ruedas") {
      const opt = WHEELCHAIR_OPTIONS.find((o) => o.size_label === size_label);
      return opt ? opt.price : 0;
    }
    // electronica · general → generic weight
    return computeGenericWeight(weight_kg);
  }
  // ropa, alimentos, herramientas, otros → generic weight
  return computeGenericWeight(weight_kg);
}

// True when the current selection unlocks a per-unit / fixed-tier flow
// (i.e. Step 2 shows option tiles rather than a weight input).
function usesTileSelector(form) {
  if (form.shipment_type === "tv") return true;
  if (form.shipment_type === "electronica") {
    return ["bicis", "microondas", "neveras", "patinetes", "silla_ruedas"].includes(
      form.shipment_subcategory
    );
  }
  return false;
}

function needsWeightInput(form) {
  if (!form.shipment_type) return false;
  if (form.shipment_type === "tv") return false;
  if (form.shipment_type === "electronica") {
    return form.shipment_subcategory === "general";
  }
  return true; // ropa, alimentos, herramientas, otros
}

// ---------- Component ----------

const STEPS = ["Tipo", "Detalles", "Remitente", "Destinatario", "Pago"];

export default function EnviarPaqueteNuevoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const preselectType = params.get("tipo");

  const [camps, setCamps] = useState([]);
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Fase 1 · Payment methods toggle — lee estado público al montar
  // y ajusta los botones "Tarjeta" / "Efectivo".
  const [paymentMethods, setPaymentMethods] = useState({
    card_enabled: true,
    cash_enabled: true,
    loaded: false,
  });

  useEffect(() => {
    api
      .get("/settings/payment-methods")
      .then((r) => {
        const card = !!r.data?.card_enabled;
        const cash = !!r.data?.cash_enabled;
        setPaymentMethods({ card_enabled: card, cash_enabled: cash, loaded: true });
        // Autoselecciona el método disponible si el actual está deshabilitado.
        setForm((f) => {
          if (f.payment_method === "tarjeta" && !card && cash) return { ...f, payment_method: "efectivo" };
          if (f.payment_method === "efectivo" && !cash && card) return { ...f, payment_method: "tarjeta" };
          return f;
        });
      })
      .catch(() => setPaymentMethods((p) => ({ ...p, loaded: true })));
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    if (params.get("cancelled") === "1") {
      toast.info("Pago cancelado. Puedes intentarlo de nuevo.");
      const next = new URLSearchParams(params);
      next.delete("cancelled");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const [form, setForm] = useState({
    shipment_type: preselectType || "",
    shipment_subcategory: "",
    size_value: null, // numeric (TV inches, bike inches, microwave litres)
    size_label: "", // display label ("Pequeña", "50 L", "22\"" …)
    weight_kg: "", // string as-typed; parsed on demand
    description: "",
    sender_name: "",
    sender_email: "",
    sender_phone: "",
    sender_address: "",
    sender_postal_code: "",
    sender_city: "",
    sender_province: "",
    recipient_name: "",
    recipient_phone: "",
    recipient_camp: "",
    recipient_daira: "",
    recipient_barrio: "",
    guest_email: "",
    payment_method: "tarjeta",
  });

  useEffect(() => {
    api.get("/delivery/camps").then((r) => setCamps(r.data.camps || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        sender_name: f.sender_name || user.name || "",
        sender_email: f.sender_email || user.email || "",
      }));
    }
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const price = useMemo(() => computePrice(form), [form]);

  // Extensión Fase 2 · Promo codes — estado del código aplicado en el
  // wizard. El descuento se calcula sobre ``price`` (el fee bruto que
  // devuelve computePrice) y ``finalPrice`` es lo que va al backend.
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState(null);

  const discountAmount = useMemo(() => {
    if (!promoApplied || !price) return 0;
    return Math.round(price * (promoApplied.discount_percent / 100) * 100) / 100;
  }, [promoApplied, price]);

  const finalPrice = useMemo(
    () => Math.max(Math.round((price - discountAmount) * 100) / 100, 0),
    [price, discountAmount]
  );

  // Si el precio bruto cambia (usuario vuelve al paso 2 y modifica el peso),
  // recalculamos el descuento contra el nuevo importe pero mantenemos el
  // código aplicado. Sin efecto secundario visible — todo pasa por useMemo.

  const applyPromo = async () => {
    if (!user) {
      setPromoError("Inicia sesión para aplicar códigos");
      return;
    }
    const code = (promoInput || "").trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const { data } = await api.post("/promo-codes/validate", {
        code,
        context: "paquete",
        subtotal: price,
      });
      setPromoApplied({
        code: data.code,
        discount_percent: data.discount_percent,
        discount_amount: data.discount_amount,
      });
      toast.success(`${data.code} aplicado (-${data.discount_percent}%)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Código no válido";
      setPromoError(typeof msg === "string" ? msg : "Código no válido");
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoInput("");
    setPromoError(null);
  };

  const selectedCategory = useMemo(
    () => CATEGORIES.find((c) => c.code === form.shipment_type),
    [form.shipment_type]
  );

  const validateStep = () => {
    if (step === 0) {
      if (!form.shipment_type) return "Selecciona un tipo de envío";
      if (form.shipment_type === "electronica" && !form.shipment_subcategory) {
        return "Selecciona un tipo de electrónica";
      }
    }
    if (step === 1) {
      if (!form.description.trim()) return "Describe el contenido del envío";
      if (usesTileSelector(form)) {
        if (form.shipment_type === "tv" && !form.size_value) {
          return "Selecciona el tamaño del televisor";
        }
        if (form.shipment_type === "electronica") {
          const sub = form.shipment_subcategory;
          if (["bicis", "microondas"].includes(sub) && !form.size_value) {
            return "Selecciona una opción de tamaño";
          }
          if (["neveras", "patinetes", "silla_ruedas"].includes(sub) && !form.size_label) {
            return "Selecciona una opción de tamaño";
          }
        }
      } else if (needsWeightInput(form)) {
        const w = parseFloat(String(form.weight_kg).replace(",", "."));
        if (!w || w < 0.1 || w > 200) return "Introduce un peso entre 0,1 y 200 kg";
      }
    }
    if (step === 2) {
      if (!form.sender_name.trim()) return "Nombre del remitente requerido";
      if (!/^\S+@\S+\.\S+$/.test(form.sender_email)) return "Email del remitente no válido";
      if (form.sender_phone.trim().length < 6) return "Teléfono del remitente no válido";
      if (!form.sender_address.trim()) return "Dirección del remitente requerida";
      if (form.sender_postal_code.trim().length < 3) return "Código postal no válido";
      if (!form.sender_city.trim()) return "Ciudad requerida";
      if (!form.sender_province.trim()) return "Provincia requerida";
    }
    if (step === 3) {
      if (!form.recipient_name.trim()) return "Nombre del destinatario requerido";
      if (form.recipient_phone.trim().length < 6) return "Teléfono del destinatario no válido";
      if (!form.recipient_camp) return "Selecciona un campamento";
      if (!form.recipient_daira.trim()) return "Introduce la daira";
      if (!form.recipient_barrio.trim()) return "Introduce el barrio";
    }
    return null;
  };

  const next = () => {
    const v = validateStep();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const prev = () => {
    setError(null);
    setStep((s) => Math.max(0, s - 1));
  };

  const submit = async () => {
    const v = validateStep();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Resolve numeric size_unit tag for backend persistence. The backend
      // derives ``electronics_subtype`` on its own from ``shipment_subcategory``,
      // so we don't need to mirror the mapping here anymore.
      let size_unit;
      if (form.shipment_type === "tv" || form.shipment_subcategory === "bicis") {
        size_unit = "pulgadas";
      } else if (form.shipment_subcategory === "microondas") {
        size_unit = "l";
      } else if (["neveras", "patinetes", "silla_ruedas"].includes(form.shipment_subcategory)) {
        size_unit = "talla";
      } else if (needsWeightInput(form)) {
        size_unit = "kg";
      }

      const parsedWeight = needsWeightInput(form)
        ? parseFloat(String(form.weight_kg).replace(",", "."))
        : undefined;

      const body = {
        payment_method: form.payment_method,
        sender_name: form.sender_name.trim(),
        sender_email: form.sender_email.trim(),
        sender_phone: form.sender_phone.trim(),
        sender_address: form.sender_address.trim(),
        sender_postal_code: form.sender_postal_code.trim(),
        sender_city: form.sender_city.trim(),
        sender_province: form.sender_province.trim(),
        recipient_name: form.recipient_name.trim(),
        recipient_phone: form.recipient_phone.trim(),
        recipient_camp: form.recipient_camp,
        recipient_daira: form.recipient_daira.trim(),
        recipient_barrio: form.recipient_barrio.trim(),
        shipment_type: form.shipment_type,
        description: form.description.trim(),
        // Wizard v2 fields
        shipment_subcategory: form.shipment_subcategory || undefined,
        size_value: form.size_value ?? undefined,
        size_unit,
        size_label: form.size_label || undefined,
        computed_price_eur: price,
      };
      if (form.shipment_type === "tv") body.tv_inches = form.size_value || undefined;
      if (parsedWeight) body.weight_kg = parsedWeight;

      if (!user) body.guest_email = form.sender_email.trim();
      // Extensión Fase 2 · Promo codes — enviar el código aplicado. El
      // backend re-valida en context="paquete" y consume atómicamente.
      if (promoApplied?.code) body.promo_code = promoApplied.code;

      if (form.payment_method === "tarjeta") {
        const { data } = await api.post("/payments/shipment/checkout", {
          origin_url: window.location.origin,
          shipment: body,
        });
        window.location.href = data.url;
        return;
      }
      const { data } = await api.post("/shipments", body);
      toast.success("Envío confirmado");
      navigate(`/envio/${data.id}`, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo confirmar el envío";
      setError(typeof msg === "string" ? msg : "Error al confirmar el envío");
      if (form.payment_method === "tarjeta") {
        toast.error(typeof msg === "string" ? msg : "Error al iniciar el pago");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="enviar-paquete-nuevo-page" className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">
      <WizardIntroSEO />
      <div className="mb-6">
        <Link to="/enviar-paquete" className="text-sm text-neutral-500 hover:underline">
          ← Volver
        </Link>
        <WizardIntro />
        <span
          data-testid="shipment-step-pill"
          className="inline-flex items-center gap-2 mt-2 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-bold"
        >
          Paso {step + 1} de {STEPS.length} · {STEPS[step]}
        </span>
      </div>

      <NextDepartureBanner />

      {/* Stepper */}
      <ol data-testid="shipment-stepper" className="flex flex-wrap gap-2 mb-8">
        {STEPS.map((label, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <li
              key={label}
              className={`flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border ${
                active
                  ? "bg-amber-500 text-white border-amber-500"
                  : done
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-white text-neutral-500 border-neutral-200"
              }`}
            >
              <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] bg-white/25">
                {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </span>
              {label}
            </li>
          );
        })}
      </ol>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        <section className="rounded-2xl border border-neutral-200 bg-white p-6 min-h-[300px]">
          {/* STEP 0 · Tipo */}
          {step === 0 && (
            <div data-testid="step-tipo">
              <h2 className="text-xl font-black tracking-tight mb-4">¿Qué vas a enviar?</h2>
              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map((c) => {
                  const selected = form.shipment_type === c.code;
                  return (
                    <button
                      key={c.code}
                      type="button"
                      data-testid={`shipment-type-${c.code}`}
                      onClick={() => {
                        set("shipment_type", c.code);
                        set("shipment_subcategory", c.code === "electronica" ? "" : "");
                        set("size_value", null);
                        set("size_label", "");
                        set("weight_kg", "");
                      }}
                      className={`flex flex-col items-center text-center rounded-2xl border-2 p-5 min-h-[140px] transition-all ${
                        selected
                          ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                          : "border-neutral-200 bg-white hover:border-amber-300"
                      }`}
                    >
                      <div className={`h-16 w-16 rounded-full ${c.bg} grid place-items-center mb-3`}>
                        <c.Icon className={`h-8 w-8 ${c.color}`} strokeWidth={1.75} />
                      </div>
                      <div className={`font-bold text-sm ${selected ? "text-amber-800" : "text-neutral-900"}`}>
                        {c.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              {form.shipment_type === "electronica" && (
                <div
                  data-testid="shipment-electronics-subcategories"
                  className="mt-6 rounded-2xl border border-neutral-200 bg-neutral-50 p-5"
                  style={{ animation: "fadeIn 0.25s ease-out" }}
                >
                  <div className="text-sm font-bold text-neutral-800 mb-3">
                    ¿Qué tipo de electrónica?
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {ELECTRONICS_SUBCATS.map((s) => {
                      const selected = form.shipment_subcategory === s.code;
                      return (
                        <button
                          key={s.code}
                          type="button"
                          data-testid={`shipment-subcategory-${s.code}`}
                          onClick={() => {
                            set("shipment_subcategory", s.code);
                            set("size_value", null);
                            set("size_label", "");
                            set("weight_kg", "");
                          }}
                          className={`flex flex-col items-center text-center rounded-xl border-2 p-3 min-h-[120px] transition-all ${
                            selected
                              ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                              : "border-neutral-200 bg-white hover:border-blue-300"
                          }`}
                        >
                          <div className={`h-12 w-12 rounded-full ${s.bg} grid place-items-center mb-2`}>
                            <s.Icon className={`h-6 w-6 ${s.color}`} strokeWidth={1.75} />
                          </div>
                          <div className="font-semibold text-xs md:text-sm leading-tight line-clamp-2 break-words">
                            {s.label}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {form.shipment_type && (
                <div
                  data-testid="shipment-medicine-notice"
                  className="mt-5 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm"
                >
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <div className="font-bold">Medicamentos permitidos</div>
                    <p>
                      Solo se permite <strong>un envase de paracetamol</strong> o{" "}
                      <strong>un envase de ibuprofeno</strong> por envío.
                    </p>
                    <p>
                      Si incluyes uno de estos medicamentos, debes describirlo
                      claramente en la <strong>nota del envío</strong> (dosis,
                      cantidad y para quién).
                    </p>
                    <p>
                      Cualquier otro medicamento o cantidad mayor será retirado
                      del paquete.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 1 · Detalles */}
          {step === 1 && (
            <div data-testid="step-detalles">
              <h2 className="text-xl font-black tracking-tight mb-4">Detalles del envío</h2>

              <DescriptionField form={form} set={set} />

              {/* Dynamic Step 2 body */}
              {form.shipment_type === "tv" && (
                <TileGroup
                  title="Tamaño del televisor"
                  hint="Precio: 2,00 € por pulgada"
                  testid="shipment-tv-size"
                  Icon={Tv}
                  color="text-pink-500"
                  bg="bg-pink-100"
                  options={TV_OPTIONS}
                  value={form.size_value}
                  labelValue={form.size_label}
                  onSelect={(o) => {
                    set("size_value", o.size_value);
                    set("size_label", o.size_label);
                  }}
                />
              )}

              {form.shipment_type === "electronica" && form.shipment_subcategory === "bicis" && (
                <TileGroup
                  title="Talla de la bici"
                  hint="Precio: 2,00 € por pulgada"
                  testid="shipment-bike-size"
                  Icon={Bike}
                  color="text-emerald-600"
                  bg="bg-emerald-100"
                  options={BIKE_OPTIONS}
                  value={form.size_value}
                  labelValue={form.size_label}
                  onSelect={(o) => {
                    set("size_value", o.size_value);
                    set("size_label", o.size_label);
                  }}
                />
              )}

              {form.shipment_type === "electronica" && form.shipment_subcategory === "microondas" && (
                <TileGroup
                  title="Litros del microondas"
                  hint="Precio: 1,00 € por litro"
                  testid="shipment-microwave-size"
                  Icon={Microwave}
                  color="text-orange-500"
                  bg="bg-orange-100"
                  options={MICROWAVE_OPTIONS}
                  value={form.size_value}
                  labelValue={form.size_label}
                  onSelect={(o) => {
                    set("size_value", o.size_value);
                    set("size_label", o.size_label);
                  }}
                />
              )}

              {form.shipment_type === "electronica" && form.shipment_subcategory === "neveras" && (
                <TileGroup
                  title="Tamaño de la nevera"
                  hint="Precio fijo por tamaño"
                  testid="shipment-fridge-size"
                  Icon={Refrigerator}
                  color="text-cyan-600"
                  bg="bg-cyan-100"
                  options={FRIDGE_OPTIONS}
                  labelValue={form.size_label}
                  onSelect={(o) => {
                    set("size_value", null);
                    set("size_label", o.size_label);
                  }}
                />
              )}

              {form.shipment_type === "electronica" && form.shipment_subcategory === "patinetes" && (
                <TileGroup
                  title="Tamaño del patinete"
                  hint="Precio fijo por tamaño"
                  testid="shipment-scooter-size"
                  Icon={PatineteIcon}
                  color="text-purple-600"
                  bg="bg-purple-100"
                  options={SCOOTER_OPTIONS}
                  labelValue={form.size_label}
                  onSelect={(o) => {
                    set("size_value", null);
                    set("size_label", o.size_label);
                  }}
                />
              )}

              {form.shipment_type === "electronica" && form.shipment_subcategory === "silla_ruedas" && (
                <TileGroup
                  title="Tamaño de la silla de ruedas"
                  hint="Precio fijo por tamaño"
                  testid="shipment-wheelchair-size"
                  Icon={Accessibility}
                  color="text-pink-500"
                  bg="bg-pink-100"
                  options={WHEELCHAIR_OPTIONS}
                  labelValue={form.size_label}
                  onSelect={(o) => {
                    set("size_value", null);
                    set("size_label", o.size_label);
                  }}
                />
              )}

              {needsWeightInput(form) && (
                <div className="mt-5 max-w-sm" data-testid="shipment-weight-block">
                  <Label htmlFor="weight_kg">
                    Peso (kg) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="weight_kg"
                    inputMode="decimal"
                    value={form.weight_kg}
                    onChange={(e) => set("weight_kg", e.target.value)}
                    placeholder="2,5"
                    data-testid="shipment-weight-kg"
                    className="mt-1 h-11 border-emerald-500 focus-visible:ring-emerald-500"
                  />
                  <p className="text-xs text-neutral-600 mt-2">
                    Precio inicial: <strong>35 €</strong> con <strong>10 kg incluidos</strong>. A
                    partir del kg 11, el precio es de <strong>2,90 € por kg</strong>.
                  </p>
                </div>
              )}

              {/* Live estimated price */}
              <div
                data-testid="shipment-fee-live"
                className="mt-6 rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-900 px-4 py-4 flex items-center justify-between"
              >
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-amber-700">
                    Precio estimado
                  </div>
                  <div className="text-xs mt-0.5">
                    Puede variar tras validación del envío
                  </div>
                </div>
                <span
                  data-testid="shipment-fee-live-value"
                  className="font-black text-3xl text-amber-800"
                >
                  {formatEUR(price)}
                </span>
              </div>
            </div>
          )}

          {/* STEP 2 · Remitente */}
          {step === 2 && (
            <div data-testid="step-remitente">
              <h2 className="text-xl font-black tracking-tight mb-2">Remitente</h2>
              <p className="text-xs text-neutral-500 mb-4">
                Los campos marcados con <span className="text-red-500">*</span> son obligatorios.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <RequiredField
                  label="Nombre y apellidos"
                  value={form.sender_name}
                  onChange={(v) => set("sender_name", v)}
                  testid="sender-name"
                />
                <RequiredField
                  label="Email"
                  type="email"
                  value={form.sender_email}
                  onChange={(v) => set("sender_email", v)}
                  testid="sender-email"
                />
                <div className="sm:col-span-2">
                  <RequiredField
                    label="Teléfono"
                    value={form.sender_phone}
                    onChange={(v) => set("sender_phone", v)}
                    testid="sender-phone"
                    placeholder="+34 600 000 000"
                    max="max-w-xs"
                  />
                </div>
                <div className="sm:col-span-2">
                  <RequiredField
                    label="Dirección (calle y número)"
                    value={form.sender_address}
                    onChange={(v) => set("sender_address", v)}
                    testid="sender-address"
                    placeholder="Calle Mayor 1, 3ºA"
                  />
                </div>
                <RequiredField
                  label="Código postal"
                  value={form.sender_postal_code}
                  onChange={(v) => set("sender_postal_code", v)}
                  testid="sender-postal-code"
                  placeholder="28001"
                />
                <RequiredField
                  label="Localidad"
                  value={form.sender_city}
                  onChange={(v) => set("sender_city", v)}
                  testid="sender-city"
                  placeholder="Madrid"
                />
                <div className="sm:col-span-2">
                  <RequiredField
                    label="Provincia"
                    value={form.sender_province}
                    onChange={(v) => set("sender_province", v)}
                    testid="sender-province"
                    max="max-w-xs"
                  />
                </div>
              </div>
              {user && (
                <p className="text-xs text-neutral-500 mt-3">
                  Datos prellenados desde tu cuenta ({user.email}).
                </p>
              )}
            </div>
          )}

          {/* STEP 3 · Destinatario */}
          {step === 3 && (
            <div data-testid="step-destinatario">
              <h2 className="text-xl font-black tracking-tight mb-2">Destinatario</h2>
              <p className="text-xs text-neutral-500 mb-4">
                Los campos marcados con <span className="text-red-500">*</span> son obligatorios.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <RequiredField
                  label="Nombre y apellidos"
                  value={form.recipient_name}
                  onChange={(v) => set("recipient_name", v)}
                  testid="recipient-name"
                />
                <RequiredField
                  label="Teléfono"
                  value={form.recipient_phone}
                  onChange={(v) => set("recipient_phone", v)}
                  testid="recipient-phone"
                />
                <div className="sm:col-span-2">
                  <Label>
                    Campamento <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={form.recipient_camp}
                    onValueChange={(v) => set("recipient_camp", v)}
                  >
                    <SelectTrigger
                      data-testid="recipient-camp"
                      className="mt-1 h-11 border-emerald-500 focus-visible:ring-emerald-500"
                    >
                      <SelectValue placeholder="Selecciona un campamento…" />
                    </SelectTrigger>
                    <SelectContent>
                      {camps.map((c) => (
                        <SelectItem
                          key={c}
                          value={c}
                          data-testid={`recipient-camp-option-${c}`}
                        >
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <RequiredField
                  label="Daira"
                  value={form.recipient_daira}
                  onChange={(v) => set("recipient_daira", v)}
                  testid="recipient-daira"
                />
                <RequiredField
                  label="Barrio"
                  value={form.recipient_barrio}
                  onChange={(v) => set("recipient_barrio", v)}
                  testid="recipient-barrio"
                />
              </div>
            </div>
          )}

          {/* STEP 4 · Pago */}
          {step === 4 && (
            <div data-testid="step-pago" className="space-y-6">
              {/* Extensión Fase 2 · Promo codes — sección prominente antes
                  del selector de método de pago. Estilo dashed emerald,
                  idéntico al de /envio/checkout para consistencia visual. */}
              {user && (
                <section
                  data-testid="wizard-promo-section"
                  className={
                    promoApplied
                      ? "rounded-2xl border-2 border-emerald-500 bg-emerald-100 p-5 sm:p-6"
                      : "rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-5 sm:p-6"
                  }
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={
                        "shrink-0 rounded-2xl p-2 sm:p-3 " +
                        (promoApplied
                          ? "bg-emerald-500 text-white"
                          : "bg-white border border-emerald-200 text-emerald-600")
                      }
                    >
                      {promoApplied ? (
                        <CheckCircle2
                          className="h-6 w-6 sm:h-8 sm:w-8"
                          strokeWidth={2.25}
                        />
                      ) : (
                        <Ticket
                          className="h-6 w-6 sm:h-8 sm:w-8"
                          strokeWidth={2.25}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg sm:text-xl font-black tracking-tight text-emerald-900">
                        1. ¿Tienes un código promocional?{" "}
                        <span className="text-emerald-700 font-semibold text-sm">
                          (opcional)
                        </span>
                      </h2>
                      <p className="text-xs sm:text-sm text-emerald-800/80 mt-1">
                        Ingresa tu código para obtener un descuento en el
                        envío de tu paquete.
                      </p>

                      {promoApplied ? (
                        <div
                          data-testid="wizard-promo-applied-chip"
                          className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-white border border-emerald-300 px-4 py-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Percent className="h-5 w-5 text-emerald-600 shrink-0" />
                            <div className="min-w-0">
                              <div className="font-black font-mono text-emerald-900 truncate">
                                {promoApplied.code}
                              </div>
                              <div className="text-xs text-emerald-700 font-semibold">
                                Aplicado · descuento del{" "}
                                {promoApplied.discount_percent}%
                              </div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={removePromo}
                            data-testid="wizard-promo-remove-btn"
                            className="rounded-full h-9 text-emerald-700 hover:text-red-600 hover:bg-red-50 font-semibold text-sm"
                            aria-label="Quitar código"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Quitar
                          </Button>
                        </div>
                      ) : (
                        <div className="mt-4 flex gap-2">
                          <Input
                            value={promoInput}
                            onChange={(e) => {
                              setPromoError(null);
                              setPromoInput(
                                e.target.value
                                  .replace(/[^A-Za-z0-9]/g, "")
                                  .toUpperCase()
                              );
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                applyPromo();
                              }
                            }}
                            maxLength={20}
                            placeholder="RAMADAN10"
                            className="h-11 font-mono uppercase bg-white border-emerald-200 focus-visible:ring-emerald-500"
                            data-testid="wizard-promo-input"
                          />
                          <Button
                            type="button"
                            onClick={applyPromo}
                            disabled={promoLoading || !promoInput.trim()}
                            data-testid="wizard-promo-apply-btn"
                            className="rounded-full h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap font-semibold"
                          >
                            {promoLoading ? "Comprobando…" : "Aplicar"}
                          </Button>
                        </div>
                      )}

                      {promoError && (
                        <div
                          data-testid="wizard-promo-error"
                          className="mt-3 text-sm text-red-700 bg-white border border-red-200 rounded-lg px-3 py-2"
                        >
                          {promoError}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              <div>
              <h2 className="text-xl font-black tracking-tight mb-3">
                {user ? "2. Método de pago" : "Método de pago"}
              </h2>

              {/* Fase 1 · Banner cuando ambos métodos están deshabilitados. */}
              {paymentMethods.loaded &&
                !paymentMethods.card_enabled &&
                !paymentMethods.cash_enabled && (
                  <div
                    data-testid="shipment-payments-unavailable-banner"
                    className="mb-4 rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-3"
                  >
                    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      <strong>Pagos temporalmente no disponibles.</strong>{" "}
                      Vuelve más tarde para completar tu envío.
                    </div>
                  </div>
                )}

              <div
                className="grid sm:grid-cols-2 gap-3 mb-4"
                role="radiogroup"
                aria-label="Forma de pago"
              >
                {paymentMethods.card_enabled && (
                <button
                  type="button"
                  role="radio"
                  aria-checked={form.payment_method === "tarjeta"}
                  onClick={() => set("payment_method", "tarjeta")}
                  data-testid="payment-method-tarjeta"
                  className={`text-left rounded-2xl border-2 p-4 transition-all ${
                    form.payment_method === "tarjeta"
                      ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                      : "border-neutral-200 hover:border-amber-300"
                  }`}
                >
                  <div className="font-bold text-sm">Tarjeta online</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    Paga ahora con tu tarjeta bancaria
                  </div>
                </button>
                )}
                {paymentMethods.cash_enabled && (
                <button
                  type="button"
                  role="radio"
                  aria-checked={form.payment_method === "efectivo"}
                  onClick={() => set("payment_method", "efectivo")}
                  data-testid="payment-method-efectivo"
                  className={`text-left rounded-2xl border-2 p-4 transition-all ${
                    form.payment_method === "efectivo"
                      ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                      : "border-neutral-200 hover:border-amber-300"
                  }`}
                >
                  <div className="font-bold text-sm">Efectivo al recoger</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    Paga al mensajero en la recogida
                  </div>
                </button>
                )}
              </div>

              {form.payment_method === "efectivo" ? (
                <div
                  data-testid="payment-efectivo-info"
                  className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 text-sm"
                >
                  Un mensajero pasará a recoger tu paquete y cobrará el importe en efectivo.
                </div>
              ) : (
                <div data-testid="payment-tarjeta-form">
                  <div className="flex items-start gap-3 text-sm bg-neutral-50 border border-neutral-100 rounded-xl px-4 py-3">
                    <Lock className="h-4 w-4 text-neutral-500 shrink-0 mt-0.5" />
                    <div className="text-neutral-600">
                      Al pulsar «Pagar con Stripe» te llevaremos a la página
                      segura de Stripe para introducir los datos de tu tarjeta.
                      MercaGo no almacena tu tarjeta.
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {error && (
            <div
              data-testid="shipment-form-error"
              className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={prev}
              disabled={step === 0}
              data-testid="shipment-prev-btn"
              className="rounded-full"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Atrás
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={next}
                data-testid="shipment-next-btn"
                className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
              >
                Continuar <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={submit}
                disabled={
                  submitting ||
                  (!paymentMethods.card_enabled && !paymentMethods.cash_enabled) ||
                  (form.payment_method === "tarjeta" && !paymentMethods.card_enabled) ||
                  (form.payment_method === "efectivo" && !paymentMethods.cash_enabled)
                }
                data-testid={
                  form.payment_method === "tarjeta"
                    ? "shipment-stripe-btn"
                    : "shipment-submit-btn"
                }
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {submitting
                  ? form.payment_method === "tarjeta"
                    ? "Redirigiendo…"
                    : "Procesando…"
                  : !paymentMethods.card_enabled && !paymentMethods.cash_enabled
                  ? "Pagos no disponibles"
                  : form.payment_method === "tarjeta"
                  ? "Pagar con Stripe"
                  : "Confirmar envío"}
              </Button>
            )}
          </div>
        </section>

        {/* Sidebar summary */}
        <aside className="lg:sticky lg:top-24 lg:self-start rounded-2xl border border-neutral-200 bg-white p-6 h-fit">
          <h2 className="text-lg font-black tracking-tight mb-3">Resumen</h2>
          <div className="space-y-2 text-sm">
            <Row label="Tipo" value={selectedCategory?.label || "—"} />
            {form.shipment_subcategory && (
              <Row
                label="Subtipo"
                value={
                  ELECTRONICS_SUBCATS.find((s) => s.code === form.shipment_subcategory)?.label ||
                  "—"
                }
              />
            )}
            {form.size_label && <Row label="Tamaño" value={form.size_label} />}
            {form.weight_kg && needsWeightInput(form) && (
              <Row label="Peso" value={`${form.weight_kg} kg`} />
            )}
            <Row label="Campamento" value={form.recipient_camp || "—"} />
            <Row label="Destinatario" value={form.recipient_name || "—"} />
          </div>
          <Separator className="my-4" />
          {/* Extensión Fase 2 · Promo codes — desglose subtotal / descuento
              cuando hay un código aplicado. Total refleja el importe final. */}
          {promoApplied && discountAmount > 0 ? (
            <div className="space-y-2 text-sm mb-2">
              <div className="flex justify-between text-neutral-600">
                <span>Subtotal envío</span>
                <span className="font-semibold">{formatEUR(price)}</span>
              </div>
              <div
                className="flex justify-between text-emerald-700"
                data-testid="wizard-summary-discount-line"
              >
                <span className="font-semibold flex items-center gap-1">
                  <Ticket className="h-3.5 w-3.5" />
                  {promoApplied.code} (-{promoApplied.discount_percent}%)
                </span>
                <span className="font-semibold">
                  -{formatEUR(discountAmount)}
                </span>
              </div>
            </div>
          ) : null}
          <div className="flex justify-between items-center">
            <span className="text-sm text-neutral-600">Total</span>
            <span data-testid="shipment-summary-fee" className="font-black text-2xl text-amber-700">
              {formatEUR(finalPrice)}
            </span>
          </div>
          <p className="mt-3 text-[11px] text-neutral-500 text-center flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" /> Pago seguro con Stripe (o efectivo al recoger)
          </p>
        </aside>
      </div>
      <WizardFaqs />
    </div>
  );
}

// ---------- Small building blocks ----------

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-neutral-500 shrink-0">{label}</span>
      <span className="font-semibold text-right truncate">{value}</span>
    </div>
  );
}

function DescriptionField({ form, set }) {
  return (
    <div className="mb-5">
      <Label htmlFor="description">
        Descripción del contenido <span className="text-red-500">*</span>
      </Label>
      <Textarea
        id="description"
        data-testid="shipment-description"
        rows={4}
        value={form.description}
        onChange={(e) => set("description", e.target.value)}
        placeholder="Detalla el contenido con la mayor precisión posible"
        maxLength={500}
        className="mt-1 border-emerald-500 focus-visible:ring-emerald-500"
      />
      <div className="text-xs text-neutral-500 mt-1">
        {form.description.length}/500 caracteres
      </div>
    </div>
  );
}

function RequiredField({ label, value, onChange, testid, placeholder, type = "text", max }) {
  return (
    <div className={max || ""}>
      <Label>
        {label} <span className="text-red-500">*</span>
      </Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        className="mt-1 h-11 border-emerald-500 focus-visible:ring-emerald-500"
      />
    </div>
  );
}

/**
 * Uniform tile picker for size/tier selection. Used by TV, bikes,
 * microwaves, fridges, scooters and wheelchairs.
 *
 * - Options with a numeric ``size_value`` compare via that key (bikes,
 *   TVs, microwaves).
 * - Fixed-tier options compare via ``size_label`` (fridges, scooters,
 *   wheelchairs).
 */

// Fase 4 · CMS — intro dinámica del wizard (title, subtitle, SEO, FAQs).
function WizardIntroSEO() {
  const { fields } = useContent("shipment_wizard");
  return (
    <PageSEO
      title={fields.seo_title || "Enviar un paquete"}
      description={fields.seo_description}
      canonical="https://mercago.one/enviar-paquete/nuevo"
    />
  );
}

function WizardIntro() {
  const { fields } = useContent("shipment_wizard");
  return (
    <>
      <h1
        data-testid="wizard-intro-title"
        className="text-3xl sm:text-4xl font-black tracking-tight mt-2"
      >
        {fields.intro_title || "Nuevo envío"}
      </h1>
      {fields.intro_subtitle && (
        <p
          data-testid="wizard-intro-subtitle"
          className="mt-2 text-sm text-neutral-600 whitespace-pre-line"
        >
          {fields.intro_subtitle}
        </p>
      )}
    </>
  );
}

export function WizardFaqs() {
  // Renderizado desde `/enviar-paquete` (landing pre-wizard). Sección FAQ
  // editable por admin. Colocada como export para poder reusarla si es
  // necesario desde otro shell del wizard.
  const { fields } = useContent("shipment_wizard");
  const items = Array.isArray(fields.faq_items) ? fields.faq_items : [];
  if (items.length === 0) return null;
  return (
    <section
      data-testid="wizard-faqs"
      className="mt-10 rounded-2xl border border-neutral-200 bg-white p-6"
    >
      <h2 className="text-xl font-black tracking-tight">Preguntas frecuentes</h2>
      <div className="mt-4 space-y-4">
        {items.map((it, i) => (
          <div key={i} data-testid={`wizard-faq-${i}`}>
            <div className="font-bold text-sm">{it.q}</div>
            <div className="text-sm text-neutral-600 mt-1 whitespace-pre-line">
              {it.a}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TileGroup({ title, hint, testid, Icon, color, bg, options, value, labelValue, onSelect }) {
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="font-bold text-sm mb-1">{title}</div>
      {hint && <div className="text-xs text-neutral-500 mb-3">{hint}</div>}
      <div className="grid sm:grid-cols-2 gap-2">
        {options.map((o) => {
          const selected =
            (o.size_value !== undefined && value === o.size_value) ||
            (o.size_value === undefined && labelValue === o.size_label);
          const testKey = o.size_value !== undefined ? o.size_value : o.size_label.toLowerCase();
          return (
            <button
              key={testKey}
              type="button"
              data-testid={`${testid}-opt-${testKey}`}
              onClick={() => onSelect(o)}
              className={`flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all ${
                selected
                  ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                  : "border-neutral-200 bg-white hover:border-amber-300"
              }`}
            >
              <div className={`h-10 w-10 rounded-full ${bg} grid place-items-center shrink-0`}>
                <Icon className={`h-5 w-5 ${color}`} strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{o.size_label}</div>
                {o.hint && (
                  <div className="text-xs text-neutral-500 leading-tight mt-0.5">
                    {o.hint}
                  </div>
                )}
              </div>
              <div className="font-black text-sm text-amber-700 shrink-0">
                {formatEUR(o.price)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
